'use client';

import { useFinancialStore } from '@/store/useFinancialStore';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';
import PremiumEmptyState from '@/components/PremiumEmptyState';
import { Link } from 'react-router-dom';
import { PortfolioPerformanceChart, AssetAllocationChart } from '@/components/DashboardCharts';
import A2ANeuralFeed from '@/components/A2ANeuralFeed';
import { motion, AnimatePresence } from 'framer-motion';

// Type definitions for dynamic datasets are handled by the store

export default function DashboardPage() {
  const { totalValue, totalPnl, totalPnlPercent, balance, positions, signals, allocationData, performanceData, isLoading, refreshDashboard } = useFinancialStore();

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <div className="space-y-12">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <motion.div 
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="space-y-1"
        >
          <div className="flex items-center gap-2">
             <div className="w-1.5 h-1.5 rounded-full bg-brand-teal shadow-glow animate-pulse" />
             <h1 className="text-sm font-black uppercase tracking-[0.3em] text-text-tertiary">Sovereign Command</h1>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-text-primary tracking-tighter">Terminal <span className="text-brand-teal-light">Overview</span></h2>
        </motion.div>
        
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex gap-4 p-2 bg-bg-secondary/40 backdrop-blur-xl rounded-[2rem] border border-white/5 shadow-inner"
        >
          <GlassButton variant="primary" size="sm" onClick={() => refreshDashboard()} className="rounded-2xl px-6 h-12 uppercase tracking-widest font-black text-[10px]">
             Force Sync
          </GlassButton>
        </motion.div>
      </header>

      {/* Primary Performance Matrix */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 lg:grid-cols-3 gap-8"
      >
        <motion.div variants={itemVariants} className="lg:col-span-2">
           <GlassCard variant="elevated" className="p-8 h-full bg-gradient-to-br from-bg-secondary/40 to-transparent">
              <div className="flex justify-between items-center mb-8">
                 <div className="space-y-1">
                    <h3 className="text-sm font-black uppercase tracking-widest text-text-tertiary">Performance Ledger</h3>
                    <p className="text-[10px] text-text-muted uppercase tracking-widest">24H Net Capital Velocity</p>
                 </div>
                 <div className="text-right">
                    <span className="text-2xl font-black text-text-primary tabular-nums tracking-tighter">${totalValue.toLocaleString()}</span>
                    <div className={`text-[10px] font-black uppercase tracking-widest flex items-center justify-end gap-1 ${totalPnl >= 0 ? 'text-brand-teal-light' : 'text-brand-plum-light'}`}>
                       <span>{totalPnl >= 0 ? '▲' : '▼'}</span>
                       <span>${Math.abs(totalPnl).toLocaleString()} ({Math.abs(totalPnlPercent)}%)</span>
                    </div>
                 </div>
              </div>
              <PortfolioPerformanceChart data={performanceData} />
           </GlassCard>
        </motion.div>

        <motion.div variants={itemVariants}>
           <GlassCard variant="elevated" className="p-8 h-full flex flex-col items-center relative overflow-hidden">
              <div className="self-start space-y-1 mb-8">
                 <h3 className="text-sm font-black uppercase tracking-widest text-text-tertiary">Treasury Exposure</h3>
                 <p className="text-[10px] text-text-muted uppercase tracking-widest">Institutional Asset Allocation</p>
              </div>
              <div className="relative w-full aspect-square max-w-[240px]">
                 <AssetAllocationChart data={allocationData} />
              </div>
              <div className="mt-8 w-full grid grid-cols-2 gap-3">
                 {allocationData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2 p-2 bg-white/5 rounded-xl border border-white/5">
                       <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                       <span className="text-[9px] font-black uppercase tracking-widest text-text-secondary truncate">{item.name}</span>
                       <span className="text-[9px] font-mono font-bold text-text-primary ml-auto">{item.value}%</span>
                    </div>
                 ))}
              </div>
           </GlassCard>
        </motion.div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
          {/* Portfolio Positions */}
          <GlassCard variant="glow" className="p-8 h-full">
            <div className="flex justify-between items-center mb-8">
              <div className="space-y-1">
                <h3 className="text-lg font-black text-text-primary flex items-center gap-2 uppercase tracking-tighter">
                  <div className="w-2 h-2 rounded-full bg-brand-teal shadow-glow" />
                  Network <span className="text-brand-teal-light">Positions</span>
                </h3>
              </div>
              <Link to="/portfolio" className="px-4 py-2 bg-white/5 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] text-text-secondary hover:text-white transition-all hover:bg-white/10">
                Full Ledger
              </Link>
            </div>
            
            {positions.length === 0 ? (
              <div className="min-h-[300px] flex items-center justify-center">
                <PremiumEmptyState title="No active positions" description="Your capital is currently sidelined. Initialize deployment to capture alpha." />
              </div>
            ) : (
              <div className="space-y-4">
                {positions.slice(0, 5).map((position) => (
                  <div key={position.symbol} className="flex justify-between items-center p-5 bg-white/[0.02] hover:bg-white/[0.05] transition-all border border-white/5 rounded-[1.5rem] group">
                    <div className="flex items-center gap-4">
                       <div className="w-10 h-10 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal-light font-black text-xl border border-brand-teal/20">
                          {position.symbol[0]}
                       </div>
                       <div>
                          <div className="font-black text-text-primary group-hover:text-brand-teal-light transition-colors uppercase tracking-tight">{position.symbol}</div>
                          <div className="text-[10px] text-text-tertiary font-bold uppercase tracking-widest">{position.quantity} Units Secured</div>
                       </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-text-primary tracking-tight">${position.totalValue.toLocaleString()}</div>
                      <div className={`text-[10px] font-black tabular-nums tracking-widest ${position.unrealizedPnl >= 0 ? 'text-brand-teal-light' : 'text-brand-plum-light'}`}>
                        {position.unrealizedPnl >= 0 ? '+' : ''}${Math.abs(position.unrealizedPnl).toLocaleString()} ({position.unrealizedPnlPercent}%)
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </motion.div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
          <A2ANeuralFeed />
        </motion.div>
      </div>
    </div>
  );
}
